import { useState, useEffect, useCallback, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiDownload, FiUpload } from 'react-icons/fi';
import {
  apiGetBooks,
  apiGetCategories,
  apiDeleteBook,
  apiExportBooksXlsx,
  apiBulkUpdateBookStatus,
} from '../api';
import { BookModal } from './books/BookModal';
import { ImportModal } from './books/ImportModal';
import { getAdminBookImage, handleAdminBookImageError } from '../utils/bookImage';
import Pagination from '../components/Pagination';
import { confirmAdminAction } from '../utils/adminConfirm';

const BooksManager = () => {
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [statusCounts, setStatusCounts] = useState(null);
  const [availability, setAvailability] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState(new Map());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const requestVersion = useRef(0);
  const searchPending = search.trim() !== debouncedSearch;
  const allPageSelected = books.length > 0 && books.every((book) => selectedBooks.has(book.id));
  const somePageSelected = books.some((book) => selectedBooks.has(book.id));

  const loadBooks = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '25',
        includeInactive: 'true',
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (availability) params.set('availability', availability);
      const booksData = await apiGetBooks(params.toString());
      if (version !== requestVersion.current) return;
      setBooks(booksData.books || []);
      setPagination(booksData.pagination || { total: 0, pages: 1 });
      setStatusCounts(booksData.statusCounts || null);
    } catch (err) {
      console.error(err);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [page, debouncedSearch, availability]);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  useEffect(() => {
    apiGetCategories()
      .then((data) => setCategories(data.categories || []))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async (id) => {
    if (bulkBusy) return;
    const confirmed = await confirmAdminAction('Are you sure you want to delete this book?', {
      title: 'Delete book', confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    try {
      await apiDeleteBook(id);
      setSelectedBooks(new Map());
      if (books.length === 1 && page > 1) setPage((current) => current - 1);
      else loadBooks();
    } catch { /* API client displays the error toast. */ }
  };

  const handleEdit = (book) => {
    setEditingBook(book);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingBook(null);
    setShowModal(true);
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditingBook(null);
    setSelectedBooks(new Map());
    loadBooks();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiExportBooksXlsx();
    } catch { /* API client displays the error toast. */ } finally {
      setExporting(false);
    }
  };

  const toggleBook = (book) => {
    if (bulkBusy || searchPending) return;
    const next = new Map(selectedBooks);
    if (next.has(book.id)) next.delete(book.id);
    else {
      if (next.size >= 500) { setBulkError('Select up to 500 books at a time.'); return; }
      next.set(book.id, book.title);
    }
    setSelectedBooks(next);
    setBulkError('');
  };

  const togglePage = () => {
    if (bulkBusy || searchPending) return;
    const next = new Map(selectedBooks);
    books.forEach((book) => {
      if (allPageSelected) next.delete(book.id);
      else next.set(book.id, book.title);
    });
    if (next.size > 500) { setBulkError('Select up to 500 books at a time.'); return; }
    setSelectedBooks(next);
    setBulkError('');
  };

  const handleBulkStatus = async (isActive) => {
    if (!selectedBooks.size || bulkBusy || searchPending) return;
    setBulkBusy(true);
    setBulkError('');
    const ids = [...selectedBooks.keys()];
    const names = [...selectedBooks.values()].slice(0, 5).join(', ');
    const label = isActive ? 'Active' : 'Inactive';
    try {
      const confirmed = await confirmAdminAction(
        `Make ${ids.length} selected ${ids.length === 1 ? 'book' : 'books'} ${label.toLowerCase()}? Selected: ${names}${ids.length > 5 ? ` and ${ids.length - 5} more` : ''}. ${isActive
          ? 'Active books appear in the shop when their category is also active.'
          : 'These books will be hidden from the shop and cannot be purchased.'}`,
        { title: `Make books ${label.toLowerCase()}`, confirmLabel: label }
      );
      if (!confirmed) return;
      await apiBulkUpdateBookStatus(ids, isActive);
      setBooks((current) => current.map((book) => selectedBooks.has(book.id) ? { ...book, isActive } : book));
      setSelectedBooks(new Map());
      await loadBooks();
    } catch (error) {
      setBulkError(error.message || 'Could not update books. Please try again.');
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading books...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-black" size={16} />
          <input
            type="text"
            placeholder="Search books..."
            value={search}
            disabled={bulkBusy}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedBooks(new Map());
              setBulkError('');
            }}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:border-orange-500"
          />
        </div>
        <select
          aria-label="Book status"
          value={availability}
          disabled={bulkBusy}
          onChange={(e) => {
            setAvailability(e.target.value);
            setPage(1);
            setSelectedBooks(new Map());
            setBulkError('');
          }}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">All books</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="out-of-stock">Out of stock</option>
        </select>
        <button
          onClick={handleAdd}
          disabled={bulkBusy}
          className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition"
        >
          <FiPlus size={16} /> Add Book
        </button>
        <button
          onClick={handleExport}
          disabled={exporting || bulkBusy}
          className="flex items-center gap-2 bg-white border border-gray-300 text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition disabled:opacity-50"
        >
          <FiDownload size={16} /> {exporting ? 'Exporting…' : 'Export Excel'}
        </button>
        <button
          onClick={() => setShowImport(true)}
          disabled={bulkBusy}
          className="flex items-center gap-2 bg-white border border-gray-300 text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
        >
          <FiUpload size={16} /> Import Excel
        </button>
      </div>

      {/* Info */}
      <p className="text-sm text-black">{pagination.total} books found</p>
      <div className="flex flex-wrap gap-3 text-sm" aria-live="polite">
        <span className="rounded-full bg-green-100 px-3 py-1 text-green-700">Active: {statusCounts?.active ?? '—'}</span>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">Inactive: {statusCounts?.inactive ?? '—'}</span>
        <span className="rounded-full bg-red-100 px-3 py-1 text-red-700">Out of stock: {statusCounts?.outOfStock ?? '—'}</span>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3" aria-busy={bulkBusy}>
        <span className="text-sm font-medium" aria-live="polite">{selectedBooks.size} {selectedBooks.size === 1 ? 'book' : 'books'} selected</span>
        <button onClick={() => handleBulkStatus(true)} disabled={!selectedBooks.size || bulkBusy || searchPending} className="rounded-lg border border-green-300 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:opacity-50">Active</button>
        <button onClick={() => handleBulkStatus(false)} disabled={!selectedBooks.size || bulkBusy || searchPending} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Inactive</button>
        <button onClick={() => { setSelectedBooks(new Map()); setBulkError(''); }} disabled={!selectedBooks.size || bulkBusy} className="rounded-lg px-3 py-2 text-sm text-black hover:bg-gray-50 disabled:opacity-50">Clear Selection</button>
        {bulkBusy && <span role="status" className="text-sm text-black">Waiting for confirmation or updating books...</span>}
        <p className="w-full text-xs text-gray-600">Select up to 500 books across pages. Select all applies to the current page. Changing the search or status filter clears your selection.</p>
      </div>
      {bulkError && <p role="alert" className="text-sm text-red-600">{bulkError}</p>}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-black">
              <tr>
                <th className="w-12 px-4 py-3 text-left">
                  <input type="checkbox" aria-label="Select all books on this page" checked={allPageSelected} disabled={!books.length || bulkBusy || searchPending} onChange={togglePage} ref={(node) => { if (node) node.indeterminate = somePageSelected && !allPageSelected; }} className="rounded border-gray-300" />
                </th>
                <th className="px-4 py-3 text-left">Book</th>
                <th className="px-4 py-3 text-left">SKU</th>
                <th className="px-4 py-3 text-left">Author</th>
                <th className="px-4 py-3 text-left">India Price</th>
                <th className="min-w-[170px] whitespace-nowrap px-4 py-3 text-left">United States Price</th>
                <th className="px-4 py-3 text-left">Stock</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-left">Rating</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {books.map((book) => (
                <tr key={book.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input type="checkbox" aria-label={`Select ${book.title}`} checked={selectedBooks.has(book.id)} disabled={bulkBusy || searchPending || (!selectedBooks.has(book.id) && selectedBooks.size >= 500)} onChange={() => toggleBook(book)} className="rounded border-gray-300" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={getAdminBookImage(book.image)}
                        onError={handleAdminBookImageError}
                        alt={`${book.title} cover`}
                        loading="lazy"
                        className="w-10 h-14 object-cover rounded border border-gray-200 bg-orange-50"
                      />
                      <div>
                        <span className="font-medium text-black line-clamp-1">{book.title}</span>
                        {book.isBestseller && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded ml-2">
                            Bestseller
                          </span>
                        )}
                        {book.isPreorder && (
                          <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-700">
                            Pre-order{book.preorderReleaseDate ? ` · ${book.preorderReleaseDate}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600"><span className="block min-w-[120px] max-w-[220px] break-all">{book.sku || '—'}</span></td>
                  <td className="px-4 py-3 text-black">{book.author}</td>
                  <td className="px-4 py-3">
                    <span className="font-medium">₹{book.price}</span>
                    {book.originalPrice && (
                      <span className="text-xs text-black line-through ml-1">
                        ₹{book.originalPrice}
                      </span>
                    )}
                    <div className="text-xs text-green-700 mt-1">
                      {Number(book.discount) || 0}% OFF
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {book.usdPrice != null ? (
                      <div>
                        <div>
                          <span className="font-medium">${book.usdPrice}</span>
                          {book.usdOriginalPrice && (
                            <span className="text-xs text-black line-through ml-1">
                              ${book.usdOriginalPrice}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-green-700">
                          {Number(book.usdDiscount) || 0}% OFF
                        </span>
                      </div>
                    ) : <span className="text-xs text-black">Not set</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`${book.stock < 10 ? 'text-red-600 font-medium' : 'text-black'}`}
                    >
                      {book.stock}
                    </span>
                    {Number(book.stock) <= 0 && <span className="mt-1 block text-xs text-red-600">Out of stock</span>}
                  </td>
                  <td className="px-4 py-3 text-black">{book.category?.name || '-'}</td>
                  <td className="px-4 py-3 text-black">{book.rating || '-'} ⭐</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${book.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{book.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => handleEdit(book)}
                      disabled={bulkBusy}
                      aria-label={`Edit ${book.title}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(book.id)}
                      disabled={bulkBusy}
                      aria-label={`Delete ${book.title}`}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {books.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-black">
                    No books found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} pages={pagination.pages} onChange={setPage} disabled={bulkBusy || searchPending} />

      {/* Modal */}
      {showModal && (
        <BookModal
          book={editingBook}
          categories={categories}
          onClose={() => {
            setShowModal(false);
            setEditingBook(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={() => {
            setShowImport(false);
            setSelectedBooks(new Map());
            loadBooks();
            apiGetCategories()
              .then((data) => setCategories(data.categories || []))
              .catch((err) => console.error(err));
          }}
        />
      )}
    </div>
  );
};

export default BooksManager;
